import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { ORIGINS, STATUS_META, MAX_PRODUCTS, gstSuffix } from "@/lib/constants";
import ProductEditor from "@/components/ProductEditor";
import ProductView from "@/components/ProductView";
import MergeProducts from "@/components/MergeProducts";
import ShareSheet from "@/components/ShareSheet";
import FileUpload from "@/components/FileUpload";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Package, ImageIcon, FileText, Download, Eye, Upload, MessageCircle, RotateCcw, Archive, PackageX, PackageCheck, X, Layers, GitMerge, AlertTriangle } from "lucide-react";

function fmtSize(n) {
  if (!n) return "";
  return n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;
}

export default function ProductLibrary() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [origin, setOrigin] = useState("all");
  const [editing, setEditing] = useState(null);
  const [pdfs, setPdfs] = useState([]);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfName, setPdfName] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [stockFilter, setStockFilter] = useState("all");
  const [binOpen, setBinOpen] = useState(false);
  const [trash, setTrash] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [addOpen, setAddOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [adding, setAdding] = useState(false);
  const [sortBy, setSortBy] = useState("price_asc");
  const [newTitle, setNewTitle] = useState("");
  const [viewing, setViewing] = useState(null);
  const [brand, setBrand] = useState(null);
  const [merging, setMerging] = useState(null);
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const loadPdfs = () => api.get("/catalog-pdfs").then((r) => setPdfs(r.data)).catch(() => {});
  useEffect(() => { loadPdfs(); api.get("/brand").then((r) => setBrand(r.data)).catch(() => {}); }, []);

  const createPdf = async () => {
    if (!pdfFile) return toast.error("Upload a PDF file first");
    try {
      await api.post("/catalog-pdfs", {
        name: pdfName || pdfFile.original_filename, file_id: pdfFile.id, file_url: pdfFile.url,
        content_type: pdfFile.content_type, size: pdfFile.size, original_filename: pdfFile.original_filename,
      });
      toast.success("Catalogue PDF added");
      setPdfOpen(false);
      setPdfName("");
      setPdfFile(null);
      loadPdfs();
    } catch (e) {
      toast.error("Could not add catalogue PDF");
    }
  };

  const delPdf = async (id) => {
    try {
      await api.delete(`/catalog-pdfs/${id}`);
      toast.success("Removed");
      loadPdfs();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove");
    }
  };

  const downloadPdf = async (p) => {
    try {
      const res = await api.get(p.file_url.replace(/^\/api/, ""), { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u;
      a.download = `${p.name || "catalogue"}.pdf`;
      a.click();
      URL.revokeObjectURL(u);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const [sharePdfItem, setSharePdfItem] = useState(null);
  const sharePdf = (p) => setSharePdfItem(p);
  const prepareCatPdf = async () => {
    const res = await api.get(sharePdfItem.file_url.replace(/^\/api/, ""), { responseType: "blob" });
    return new File([res.data], `${sharePdfItem.name || "catalogue"}.pdf`, { type: "application/pdf" });
  };

  const load = () => {
    api.get("/products", { params: { search: search || undefined, origin: origin === "all" ? undefined : origin, stock_status: stockFilter === "all" ? undefined : stockFilter } })
      .then((r) => setRows(r.data))
      .catch(() => {});
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, origin, stockFilter]); // eslint-disable-line

  const save = async (p) => {
    try {
      if (p.id) {
        const { id, is_deleted, created_by, created_at, ...rest } = p;
        await api.put(`/products/${p.id}`, rest);
      } else {
        await api.post("/products", p);
      }
      toast.success("Saved");
      load();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
      return false;
    }
  };

  const moveToBin = async (id) => {
    await api.delete(`/products/${id}`);
    toast.success("Moved to Recycle Bin");
    load();
  };

  const toggleStock = async (p) => {
    const next = (p.stock_status || "in_stock") === "out_of_stock" ? "in_stock" : "out_of_stock";
    await api.put(`/products/${p.id}`, { stock_status: next });
    toast.success(next === "out_of_stock" ? "Marked out of stock" : "Marked in stock");
    load();
  };

  const loadTrash = () => api.get("/products/trash").then((r) => setTrash(r.data)).catch(() => {});
  const openBin = () => { loadTrash(); setBinOpen(true); };
  const restore = async (id) => { await api.post(`/products/${id}/restore`); toast.success("Restored"); loadTrash(); load(); };
  const deleteForever = async (id) => {
    await api.delete(`/products/${id}`, { params: { permanent: true } });
    toast.success("Permanently deleted");
    setDelTarget(null);
    loadTrash();
  };

  // grade-wise groups, sortable within each grade (no price last)
  const grouped = useMemo(() => {
    const groups = {};
    for (const p of rows) {
      const g = (p.grade || "").trim() || "Ungraded";
      (groups[g] || (groups[g] = [])).push(p);
    }
    const priceVal = (p) => (p.price_per_kg == null || p.price_per_kg === "" ? null : Number(p.price_per_kg));
    const cmp = (a, b) => {
      if (sortBy === "most_used") {
        const d = (b.times_added || 0) - (a.times_added || 0);
        if (d !== 0) return d;
      }
      const pa = priceVal(a), pb = priceVal(b);
      if (pa == null && pb == null) return 0;
      if (pa == null) return 1;
      if (pb == null) return -1;
      return sortBy === "price_desc" ? pb - pa : pa - pb;
    };
    const keys = Object.keys(groups).sort((a, b) => (a === "Ungraded" ? 1 : b === "Ungraded" ? -1 : a.localeCompare(b)));
    return keys.map((k) => ({ grade: k, items: groups[k].slice().sort(cmp) }));
  }, [rows, sortBy]);

  const flat = useMemo(() => grouped.flatMap((g) => g.items), [grouped]);
  const viewIdx = viewing ? flat.findIndex((x) => x.id === viewing.id) : -1;

  // duplicate-lot groups (same lot number, ignoring case/spaces)
  const dupGroups = useMemo(() => {
    const m = {};
    for (const p of rows) {
      const k = String(p.lot_no || "").replace(/\s+/g, "").toUpperCase();
      if (k) (m[k] || (m[k] = [])).push(p);
    }
    return Object.values(m).filter((g) => g.length > 1);
  }, [rows]);
  const dupKeyOf = (p) => String(p.lot_no || "").replace(/\s+/g, "").toUpperCase();
  const dupGroupFor = (p) => dupGroups.find((g) => dupKeyOf(g[0]) === dupKeyOf(p));

  const sortLabel = { price_asc: "price low → high", price_desc: "price high → low", most_used: "most used first" }[sortBy];

  const toggleSelect = (id) =>
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSelection = () => setSelected(new Set());

  const downloadSheets = async (format) => {
    if (selected.size === 0) return;
    toast.loading("Generating sheets…", { id: "sheets" });
    try {
      const res = await api.post("/products/export/sheets", { product_ids: [...selected], format }, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = u;
      a.download = format === "zip" ? "tasting-sheets.zip" : "tasting-sheets.pdf";
      a.click();
      URL.revokeObjectURL(u);
      toast.success("Sheets ready", { id: "sheets" });
    } catch (e) {
      toast.error("Export failed", { id: "sheets" });
    }
  };

  const gradeState = (items) => {
    const sel = items.filter((p) => selected.has(p.id)).length;
    return sel === 0 ? false : sel === items.length ? true : "indeterminate";
  };
  const toggleGrade = (items) => {
    const allSel = items.every((p) => selected.has(p.id));
    setSelected((s) => {
      const n = new Set(s);
      items.forEach((p) => (allSel ? n.delete(p.id) : n.add(p.id)));
      return n;
    });
  };

  const openAdd = async () => {
    try {
      const [d, r] = await Promise.all([
        api.get("/catalogues", { params: { status: "draft" } }),
        api.get("/catalogues", { params: { status: "ready" } }),
      ]);
      setCats([...(d.data || []), ...(r.data || [])]);
    } catch (e) { setCats([]); }
    setAddOpen(true);
  };

  const addToCatalogue = async (cid) => {
    setAdding(true);
    try {
      const { data } = await api.post(`/catalogues/${cid}/add-products`, { product_ids: [...selected] });
      if (data.added > 0) toast.success(`Added ${data.added} tea${data.added > 1 ? "s" : ""}${data.skipped ? ` · ${data.skipped} skipped (${MAX_PRODUCTS}-product limit)` : ""}`);
      else toast.error("Nothing added — that catalogue is full");
      setAddOpen(false);
      clearSelection();
      load();
    } catch (e) { toast.error("Could not add to catalogue"); }
    finally { setAdding(false); }
  };

  const createAndAdd = async () => {
    setAdding(true);
    let cat;
    try {
      ({ data: cat } = await api.post("/catalogues", newTitle.trim() ? { title: newTitle.trim() } : {}));
    } catch (e) {
      setAdding(false);
      return toast.error("Could not create catalogue");
    }
    try {
      const { data } = await api.post(`/catalogues/${cat.id}/add-products`, { product_ids: [...selected] });
      toast.success(`Created ${cat.catalogue_number || "catalogue"} with ${data.added} tea${data.added > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error("Catalogue created, but adding teas failed — add them in the editor");
    } finally {
      setAddOpen(false);
      clearSelection();
      setNewTitle("");
      setAdding(false);
      navigate(`/catalogue/${cat.id}`);
    }
  };

  return (
    <div className="pgt-fade-up space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A34A] font-semibold">Reusable teas</div>
          <h1 className="font-display text-4xl text-[#0B3B2E]">Product Library</h1>
        </div>
        <Button onClick={() => setEditing({})} data-testid="lib-add-product" className="bg-[#0B3B2E] hover:bg-[#14513D] gap-2"><Plus className="h-4 w-4" /> Add Product</Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#14513D]/50" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search garden, invoice or lot…" className="pl-10" data-testid="lib-search" />
        </div>
        <Select value={origin} onValueChange={setOrigin}>
          <SelectTrigger className="w-44" data-testid="lib-filter-origin"><SelectValue placeholder="Origin" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All origins</SelectItem>
            {ORIGINS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={stockFilter} onValueChange={setStockFilter}>
          <SelectTrigger className="w-40" data-testid="lib-filter-stock"><SelectValue placeholder="Stock" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All stock</SelectItem>
            <SelectItem value="in_stock">In stock</SelectItem>
            <SelectItem value="out_of_stock">Out of stock</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-48" data-testid="lib-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="price_asc">Price: low → high</SelectItem>
            <SelectItem value="price_desc">Price: high → low</SelectItem>
            <SelectItem value="most_used">Most used</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={openBin} data-testid="lib-recycle-bin" className="gap-2"><Archive className="h-4 w-4" /> Recycle Bin</Button>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[#C9A34A]/40 bg-[#FAF7F0] py-16 text-center">
          <Package className="h-10 w-10 mx-auto text-[#C9A34A]/60" />
          <div className="mt-3 text-[#14513D]/70">No products in the library.</div>
        </div>
      ) : (
        <div className="space-y-8">
          {dupGroups.length > 0 && (
            <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 flex flex-wrap items-center gap-3" data-testid="lib-dup-banner">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
              <div className="text-sm text-red-800 font-medium">
                {dupGroups.length} duplicate lot{dupGroups.length > 1 ? "s" : ""} in the library
                {isAdmin ? " — merge each pair into one record." : " — ask an administrator to merge them."}
              </div>
              <div className="flex flex-wrap gap-2 ml-auto">
                {dupGroups.map((g) => (
                  <Button key={dupKeyOf(g[0])} size="sm" variant="outline" disabled={!isAdmin} onClick={() => setMerging(g)} data-testid={`lib-dup-merge-${dupKeyOf(g[0])}`} className="h-8 gap-1 border-red-300 text-red-700 hover:bg-red-100 bg-white">
                    <GitMerge className="h-3.5 w-3.5" /> Lot {g[0].lot_no} · {g.length} copies
                  </Button>
                ))}
              </div>
            </div>
          )}
          {selected.size > 0 && (
            <div className="sticky top-2 z-20 flex flex-wrap items-center gap-3 rounded-xl bg-[#0B3B2E] text-[#F7F2E7] px-4 py-2.5 shadow-lg" data-testid="lib-selection-bar">
              <span className="font-semibold" data-testid="lib-selection-count">{selected.size} selected</span>
              <Button size="sm" onClick={openAdd} data-testid="lib-add-to-cat" className="bg-[#C9A34A] text-[#0B3B2E] hover:bg-[#E3C77C] gap-1"><Plus className="h-4 w-4" /> Add to catalogue</Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="lib-sheets-btn" className="bg-transparent border-[#F7F2E7]/40 text-[#F7F2E7] hover:bg-[#F7F2E7]/10 hover:text-white gap-1"><Download className="h-4 w-4" /> Download sheets</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem onClick={() => downloadSheets("pdf")} data-testid="lib-sheets-pdf"><FileText className="h-4 w-4 mr-2" /> Combined PDF</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => downloadSheets("zip")} data-testid="lib-sheets-zip"><Layers className="h-4 w-4 mr-2" /> JPG pages (ZIP)</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button size="sm" variant="ghost" onClick={clearSelection} className="text-[#F7F2E7] hover:text-white gap-1" data-testid="lib-selection-clear"><X className="h-4 w-4" /> Clear</Button>
            </div>
          )}
          {grouped.map((g) => (
            <div key={g.grade} data-testid={`lib-grade-${g.grade}`}>
              <div className="flex items-center gap-3 mb-3">
                <Checkbox checked={gradeState(g.items)} onCheckedChange={() => toggleGrade(g.items)} data-testid={`lib-grade-check-${g.grade}`} />
                <Layers className="h-4 w-4 text-[#C9A34A]" />
                <h2 className="font-display text-xl font-bold text-[#0B3B2E]">{g.grade}</h2>
                <span className="text-xs text-[#14513D]/60">{g.items.length} tea{g.items.length > 1 ? "s" : ""} · {sortLabel}</span>
                <div className="flex-1 h-px bg-[#C9A34A]/25" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {g.items.map((p) => (
                  <Card key={p.id} data-testid={`lib-card-${p.id}`} className={`p-4 border-[#C9A34A]/25 flex gap-3 ${p.stock_status === "out_of_stock" ? "opacity-75" : ""} ${selected.has(p.id) ? "ring-2 ring-[#C9A34A]" : ""}`}>
                    <div className="flex flex-col items-center gap-2">
                      <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleSelect(p.id)} data-testid={`lib-check-${p.id}`} />
                      <div onClick={() => setViewing(p)} className="h-24 w-16 shrink-0 rounded-lg overflow-hidden bg-[#0B3B2E]/5 border border-[#C9A34A]/20 grid place-items-center cursor-pointer">
                        {p.liquor_image_url ? <img src={fileUrl(p.liquor_image_url)} alt="" className="h-full w-full object-contain" /> : <ImageIcon className="h-6 w-6 text-[#C9A34A]/40" />}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div onClick={() => setViewing(p)} className="cursor-pointer group" data-testid={`lib-open-${p.id}`} title="View product with liquor image & tasting report">
                        <div className="font-semibold text-[#0B3B2E] truncate group-hover:underline" data-testid={`lib-name-${p.id}`}>{p.brand_group || p.garden || p.name}</div>
                        {p.brand_group && p.garden && <div className="text-[11px] text-[#14513D]/70 truncate">{p.garden}</div>}
                        <div className="text-xs text-[#14513D]/60 truncate">{[p.tea_category, p.price_per_kg ? `₹${p.price_per_kg}/kg${gstSuffix(p)}` : null].filter(Boolean).join(" · ") || "—"}</div>
                        <div className="text-xs text-[#14513D]/80 truncate mt-0.5" data-testid={`lib-lot-${p.id}`}>Lot: <span className="font-mono-id">{p.lot_no || "—"}</span></div>
                        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className="bg-[#C9A34A]/15 text-[#0B3B2E] border-[#C9A34A]/40 text-[10px]">{p.origin || "—"}</Badge>
                          <Badge variant="outline" className="bg-[#0B3B2E]/5 text-[#14513D] border-[#14513D]/25 text-[10px]" data-testid={`lib-used-${p.id}`}>Added {p.times_added || 0}×</Badge>
                          {p.stock_status === "out_of_stock" && <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-[10px]" data-testid={`lib-oos-${p.id}`}>Out of stock</Badge>}
                          {dupGroupFor(p) && <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 text-[10px] gap-1" data-testid={`lib-dup-${p.id}`}><AlertTriangle className="h-3 w-3" /> Duplicate lot</Badge>}
                        </div>
                      </div>
                      <div className="mt-2 flex gap-1 flex-wrap">
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditing(p)} data-testid={`lib-edit-${p.id}`}><Pencil className="h-3.5 w-3.5 mr-1" /> Edit</Button>
                        {isAdmin && dupGroupFor(p) && (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-700" onClick={() => setMerging(dupGroupFor(p))} data-testid={`lib-merge-${p.id}`}><GitMerge className="h-3.5 w-3.5 mr-1" /> Merge</Button>
                        )}
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#14513D]" onClick={() => toggleStock(p)} data-testid={`lib-stock-${p.id}`}>
                          {p.stock_status === "out_of_stock" ? <><PackageCheck className="h-3.5 w-3.5 mr-1" /> In stock</> : <><PackageX className="h-3.5 w-3.5 mr-1" /> Out of stock</>}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-600" onClick={() => moveToBin(p.id)} data-testid={`lib-del-${p.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" /> Delete</Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Catalogue PDFs — separate list-view section */}
      <div className="pt-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="font-display text-2xl text-[#0B3B2E]">Catalogue PDFs</h2>
            <p className="text-sm text-[#14513D]/70">Upload ready-made catalogue PDFs to keep alongside your product library.</p>
          </div>
          <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0B3B2E] hover:bg-[#14513D] gap-2" data-testid="catpdf-add"><Upload className="h-4 w-4" /> Upload Catalogue PDF</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Upload Catalogue PDF</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Catalogue name</Label>
                  <Input value={pdfName} onChange={(e) => setPdfName(e.target.value)} placeholder="e.g. Autumn 2026 CTC Catalogue" data-testid="catpdf-name" />
                </div>
                <FileUpload label="PDF file" accept=".pdf" purpose="catalog-pdf" testid="catpdf-file" heightClass="h-40" value={pdfFile} onChange={setPdfFile} />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPdfOpen(false)}>Cancel</Button>
                <Button onClick={createPdf} className="bg-[#0B3B2E] hover:bg-[#14513D]" data-testid="catpdf-save">Add to Library</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {pdfs.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#C9A34A]/40 bg-[#FAF7F0] py-12 text-center">
            <FileText className="h-10 w-10 mx-auto text-[#C9A34A]/60" />
            <div className="mt-3 text-[#14513D]/70">No catalogue PDFs uploaded yet.</div>
          </div>
        ) : (
          <div className="rounded-xl border border-[#C9A34A]/25 overflow-hidden divide-y divide-[#C9A34A]/15" data-testid="catpdf-list">
            {pdfs.map((p) => (
              <div key={p.id} data-testid={`catpdf-row-${p.id}`} className="flex items-center gap-3 bg-[#FAF7F0] px-4 py-3 hover:bg-[#C9A34A]/5 transition-colors">
              <div className="h-14 w-11 shrink-0 rounded-md overflow-hidden bg-[#0B3B2E] grid place-items-center border border-[#C9A34A]/30">
                {p.thumb_url ? <img src={fileUrl(p.thumb_url)} alt="" className="h-full w-full object-cover" /> : <FileText className="h-5 w-5 text-[#C9A34A]" />}
              </div>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-[#0B3B2E] truncate">{p.name}</div>
                  <div className="text-xs text-[#14513D]/60 truncate">
                    {fmtSize(p.size)} · uploaded by {p.uploaded_by || "—"} · {new Date(p.created_at).toLocaleDateString()}
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs" onClick={() => window.open(fileUrl(p.file_url), "_blank")} data-testid={`catpdf-view-${p.id}`}><Eye className="h-3.5 w-3.5" /> Preview</Button>
                <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs" onClick={() => downloadPdf(p)} data-testid={`catpdf-download-${p.id}`}><Download className="h-3.5 w-3.5" /> Download</Button>
                <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs text-[#128C4A]" onClick={() => sharePdf(p)} data-testid={`catpdf-share-${p.id}`}><MessageCircle className="h-3.5 w-3.5" /> Share</Button>
                <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs text-red-600" onClick={() => delPdf(p.id)} data-testid={`catpdf-del-${p.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {editing && (
        <ProductEditor open={!!editing} onOpenChange={(o) => !o && setEditing(null)} product={editing} onSave={save} />
      )}

      <MergeProducts items={merging} open={!!merging} onOpenChange={(o) => !o && setMerging(null)} onMerged={load} />
      <ShareSheet
        open={!!sharePdfItem}
        onOpenChange={(o) => !o && setSharePdfItem(null)}
        title={`${sharePdfItem?.name || "Catalogue"} PDF`}
        message={`Please find attached the Prime Gold Tea catalogue: ${sharePdfItem?.name || ""}. Kindly review the products and reply with the ones you would like to sample or discuss.`}
        prepare={prepareCatPdf}
      />

      <ProductView
        product={viewing}
        brand={brand}
        open={!!viewing}
        onOpenChange={(o) => !o && setViewing(null)}
        onEdit={() => { const p = viewing; setViewing(null); setEditing(p); }}
        onPrev={() => viewIdx > 0 && setViewing(flat[viewIdx - 1])}
        onNext={() => viewIdx >= 0 && viewIdx < flat.length - 1 && setViewing(flat[viewIdx + 1])}
        hasPrev={viewIdx > 0}
        hasNext={viewIdx >= 0 && viewIdx < flat.length - 1}
      />

      {/* Add selected products to a catalogue */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Add {selected.size} tea{selected.size > 1 ? "s" : ""} to a catalogue</DialogTitle></DialogHeader>
          <p className="text-sm text-[#14513D]/70">Create a new catalogue from these teas, or add them to an existing Draft/Ready one. Up to {MAX_PRODUCTS} products per catalogue.</p>
          <div className="rounded-lg border border-[#C9A34A]/40 bg-[#0B3B2E]/5 p-3">
            <div className="text-sm font-semibold text-[#0B3B2E] mb-2">Create a new catalogue</div>
            <div className="flex gap-2">
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Catalogue title (optional)" data-testid="lib-new-cat-title" />
              <Button onClick={createAndAdd} disabled={adding} data-testid="lib-new-cat-btn" className="bg-[#0B3B2E] hover:bg-[#14513D] gap-1 shrink-0"><Plus className="h-4 w-4" /> Create & add</Button>
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs text-[#14513D]/50">
            <div className="flex-1 h-px bg-[#C9A34A]/25" /> or add to an existing catalogue <div className="flex-1 h-px bg-[#C9A34A]/25" />
          </div>
          {cats.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#C9A34A]/40 bg-[#FAF7F0] py-10 text-center text-sm text-[#14513D]/70">
              No editable (Draft or Ready) catalogues yet — use “Create a new catalogue” above.
            </div>
          ) : (
            <div className="rounded-lg border border-[#C9A34A]/25 divide-y divide-[#C9A34A]/15" data-testid="lib-cat-list">
              {cats.map((c) => {
                const count = (c.products || []).length;
                const full = count >= MAX_PRODUCTS;
                return (
                  <button
                    key={c.id}
                    disabled={adding || full}
                    onClick={() => addToCatalogue(c.id)}
                    data-testid={`lib-cat-${c.id}`}
                    className="w-full text-left px-4 py-3 bg-[#FAF7F0] hover:bg-[#C9A34A]/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <div className="font-semibold text-[#0B3B2E] truncate">{c.title || c.catalogue_number}</div>
                    <div className="text-xs text-[#14513D]/60 truncate">
                      <span className="font-mono-id">{c.catalogue_number}</span> · {STATUS_META[c.status]?.label || c.status} · {count}/{MAX_PRODUCTS} products{full ? " · full" : ""}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Recycle Bin */}
      <Dialog open={binOpen} onOpenChange={setBinOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B3B2E]">Recycle Bin</DialogTitle></DialogHeader>
          <p className="text-sm text-[#14513D]/70">Deleted teas are kept here and permanently removed automatically after 30 days. Restore any tea, or delete it forever now.</p>
          {trash.length === 0 ? (
            <div className="rounded-xl border border-dashed border-[#C9A34A]/40 bg-[#FAF7F0] py-12 text-center">
              <Archive className="h-10 w-10 mx-auto text-[#C9A34A]/60" />
              <div className="mt-3 text-[#14513D]/70">Recycle Bin is empty.</div>
            </div>
          ) : (
            <div className="rounded-lg border border-[#C9A34A]/25 divide-y divide-[#C9A34A]/15" data-testid="bin-list">
              {trash.map((p) => (
                <div key={p.id} data-testid={`bin-row-${p.id}`} className="flex items-center gap-3 bg-[#FAF7F0] px-3 py-2.5">
                  <div className="h-11 w-11 shrink-0 rounded-md overflow-hidden bg-[#0B3B2E]/5 border border-[#C9A34A]/20 grid place-items-center">
                    {p.liquor_image_url ? <img src={fileUrl(p.liquor_image_url)} alt="" className="h-full w-full object-contain" /> : <ImageIcon className="h-5 w-5 text-[#C9A34A]/40" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-[#0B3B2E] truncate">{p.garden || p.name}</div>
                    <div className="text-xs text-[#14513D]/60 truncate">
                      {[p.grade, `Lot ${p.lot_no || "—"}`].filter(Boolean).join(" · ")} · deleted {p.deleted_at ? new Date(p.deleted_at).toLocaleDateString() : "—"}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => restore(p.id)} data-testid={`bin-restore-${p.id}`} className="gap-1"><RotateCcw className="h-3.5 w-3.5" /> Restore</Button>
                  <Button size="sm" variant="ghost" onClick={() => setDelTarget(p)} data-testid={`bin-delete-${p.id}`} className="gap-1 text-red-600"><Trash2 className="h-3.5 w-3.5" /> Delete forever</Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="lib-del-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl text-[#0B3B2E]">Delete this tea forever?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-[#0B3B2E]">{delTarget?.garden || delTarget?.name}</span>
              {delTarget?.lot_no ? <> (Lot {delTarget.lot_no})</> : null} will be permanently removed from the database. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lib-del-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteForever(delTarget.id)} className="bg-red-600 hover:bg-red-700" data-testid="lib-del-confirm-btn">Delete forever</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
